import { useEffect, useRef } from 'react'

import { useRecoilCallback, useRecoilValue } from 'recoil'

import {
  execTaskCallback,
  pollingCallback,
  resetCallback,
} from 'Hooks/useAsyncTask/callbacks'
import { asyncTaskState } from 'Hooks/useAsyncTask/state'
import type { AsyncTaskState } from 'Hooks/useAsyncTask/types'

interface useAsyncTaskParams {
  key?: string
  initialDelay?: number
  maxDelay?: number
  launchTask: () => Promise<number>
}

type useAsyncTaskResult = Pick<AsyncTaskState, 'metadata' | 'status'> & {
  init: () => Promise<void>
  cleanup: () => Promise<void>
}

const useAsyncTask = ({
  launchTask,
  ...p
}: useAsyncTaskParams): useAsyncTaskResult => {
  const key = p.key !== undefined ? p.key : 'stateId'
  const initialDelay = p.initialDelay !== undefined ? p.initialDelay : 500
  const maxDelay = p.maxDelay !== undefined ? p.maxDelay : 5000

  const timer = useRef<number | undefined>(undefined)
  const delay = useRef(initialDelay)

  const { idTask, pollingIteration, metadata, status } = useRecoilValue(
    asyncTaskState(key),
  )

  const execTask = useRecoilCallback(execTaskCallback, [])
  const polling = useRecoilCallback(pollingCallback, [])
  const reset = useRecoilCallback(resetCallback, [])

  const init = async (): Promise<void> => {
    await execTask(launchTask, key)
  }

  const cleanup = async (): Promise<void> => {
    window.clearTimeout(timer.current)
    delay.current = initialDelay

    await reset(key)
  }

  useEffect(() => {
    if (idTask !== undefined && idTask > 0 && pollingIteration > 0) {
      timer.current = window.setTimeout(
        polling as unknown as () => void,
        delay.current,
        key,
      )
      delay.current = Math.min(delay.current * 2, maxDelay)
    }
  }, [idTask, pollingIteration])

  return {
    metadata,
    status,
    init,
    cleanup,
  }
}

export default useAsyncTask
